import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Package, ShoppingCart, Wrench, FileText, RefreshCcw, Warehouse, MessageSquare, Plus, Clock, User,
  Trash2, ListChecks, Filter, ArrowUpDown, Paperclip, Link2 as LinkIcon, X, Search, ExternalLink, Upload,
  Euro, BellRing, ClipboardList, Calendar, LayoutGrid, List, Receipt, ChevronLeft, ChevronRight,
} from "lucide-react";
import { Layout } from "../components/layout/Layout";
import { api, formatApiError, UPLOADS_BASE } from "../lib/api";
import { useAuth, hasPerm, moduleVisible } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { toast } from "sonner";
import { DOW_LABELS, MONTH_LABELS, buildMonthGrid, rdvVisualStatus, useNow } from "../lib/calendarUtils";

// Classes par statut visuel (voir lib/calendarUtils.js > rdvVisualStatus) —
// mêmes couleurs que l'onglet Vente et "Prise de rdv" de la Caisse
// (CaissePage.jsx / PriseRdvTab.jsx), pour qu'un même rendez-vous ait
// toujours la même couleur partout où il apparaît.
const RDV_STATUS_CHIP_CLASSES = {
  termine: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  en_retard: "bg-red-500/15 text-red-700 dark:text-red-400",
  superposition: "bg-violet-500/15 text-violet-700 dark:text-violet-400",
  normal: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
};

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-xl border border-border bg-card p-6 h-full transition-transform duration-200 hover:-translate-y-0.5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <Icon className="text-primary" size={22} />
        <span className="text-3xl font-heading font-bold">{value}</span>
      </div>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

function OverviewCard({ icon: Icon, label, value, tone = "default", to, testId }) {
  const toneClass = {
    default: "border-border bg-card",
    warning: "border-amber-500/40 bg-amber-500/5",
  }[tone];
  const content = (
    <div className={`rounded-xl border p-4 flex items-center gap-3 h-full transition-transform duration-200 hover:-translate-y-0.5 ${toneClass}`} data-testid={testId}>
      <div className={`shrink-0 rounded-lg p-2 ${tone === "warning" ? "bg-amber-500/15 text-amber-600 dark:text-amber-400" : "bg-primary/10 text-primary"}`}>
        <Icon size={18} />
      </div>
      <div className="min-w-0">
        <p className="text-xl font-heading font-bold leading-tight">{value}</p>
        <p className="text-xs text-muted-foreground truncate">{label}</p>
      </div>
    </div>
  );
  return to ? <Link to={to}>{content}</Link> : content;
}

const TASK_COLUMNS = [
  { value: "non_assigne", label: "Non assigné" },
  { value: "en_cours", label: "En cours" },
  { value: "en_attente", label: "En attente" },
  { value: "termine", label: "Terminé" },
];

// Statuts que l'utilisateur peut choisir manuellement : "non_assigne" en est
// volontairement exclu, ce statut découle automatiquement de l'assignation.
const MANUAL_STATUTS = TASK_COLUMNS.filter((c) => c.value !== "non_assigne");

const STATUT_BADGE = {
  non_assigne: "bg-secondary text-muted-foreground border-border",
  en_attente: "bg-slate-500/10 text-slate-600 border-slate-500/30 dark:text-slate-300",
  en_cours: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  termine: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
};

const DOCUMENT_TYPES = [
  { value: "devis", label: "Devis" },
  { value: "reprise", label: "Reprise" },
  { value: "intervention", label: "Intervention" },
];

// Convertit un temps estimé saisi en texte libre ("2h", "1h30", "45 min", "1j") en
// nombre de minutes, pour calculer l'avancement de la durée. Renvoie null si le
// texte n'est pas reconnu (dans ce cas, pas de code couleur affiché).
function parseDureeMinutes(text) {
  if (!text) return null;
  const s = text.toLowerCase().replace(",", ".").trim();
  let m = s.match(/(\d+(?:\.\d+)?)\s*h(?:eure)?s?\s*(\d+)?/);
  if (m) return parseFloat(m[1]) * 60 + (m[2] ? parseInt(m[2], 10) : 0);
  m = s.match(/(\d+(?:\.\d+)?)\s*(?:min|minute)s?/);
  if (m) return parseFloat(m[1]);
  m = s.match(/(\d+(?:\.\d+)?)\s*j(?:our)?s?/);
  if (m) return parseFloat(m[1]) * 8 * 60; // 1 jour = 8h de travail
  m = s.match(/^(\d+(?:\.\d+)?)$/);
  if (m) return parseFloat(m[1]);
  return null;
}

// Code couleur de durée : 0-50% du temps estimé = bleu, 50-100% = orange,
// au-delà de 100% = rouge avec la mention "En retard". Le chrono démarre au
// moment de l'assignation (assigned_at), pas à la création de la tâche.
function dureeInfo(t) {
  if (t.statut === "non_assigne" || t.statut === "termine") return null;
  const startIso = t.assigned_at || t.created_at;
  const budgetMin = parseDureeMinutes(t.temps_estime);
  if (!startIso || !budgetMin) return null;
  const elapsedMin = (Date.now() - new Date(startIso).getTime()) / 60000;
  const pct = (elapsedMin / budgetMin) * 100;
  if (pct >= 100) return { pct, label: "En retard", className: "text-red-600 bg-red-500/10 border-red-500/30" };
  if (pct >= 50) return { pct, label: `${Math.round(pct)}% du temps`, className: "text-orange-600 bg-orange-500/10 border-orange-500/30" };
  return { pct, label: `${Math.round(pct)}% du temps`, className: "text-blue-600 bg-blue-500/10 border-blue-500/30" };
}

function timeAgo(iso) {
  if (!iso) return "";
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.floor(h / 24);
  return `il y a ${d} j`;
}

// --- Actions rapides : raccourcis vers la création des documents les plus
// courants. Chacune est masquée si l'utilisateur n'a pas la permission de
// création correspondante, pour ne jamais afficher un raccourci qui mènerait
// à un formulaire inutilisable.
function QuickActions({ user, modules }) {
  const navigate = useNavigate();
  const actions = [
    { key: "caisse", icon: ShoppingCart, label: "Nouvelle vente", to: "/caisse", perm: hasPerm(user, "caisse", "access") },
    { key: "devis", icon: FileText, label: "Nouveau devis", to: "/devis/new", perm: moduleVisible(modules, "devis") && hasPerm(user, "devis", "create") },
    { key: "reprise", icon: RefreshCcw, label: "Nouvelle reprise", to: "/reprises/new", perm: moduleVisible(modules, "reprise") && hasPerm(user, "reprise", "create") },
    { key: "intervention", icon: Wrench, label: "Nouvelle intervention", to: "/interventions/new", perm: moduleVisible(modules, "intervention") && hasPerm(user, "intervention", "create") },
  ].filter((a) => a.perm);

  if (actions.length === 0) return null;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="dashboard-quick-actions">
      {actions.map((a) => (
        <button
          key={a.key}
          onClick={() => navigate(a.to)}
          data-testid={`quick-action-${a.key}`}
          className="rounded-xl border border-border bg-card p-4 flex flex-col items-center justify-center gap-2 text-sm font-medium hover:-translate-y-0.5 hover:border-primary/50 transition-all duration-200"
        >
          <a.icon size={20} className="text-primary" />
          {a.label}
        </button>
      ))}
    </div>
  );
}

// --- Agenda du mois : regroupe par jour les interventions dont le
// rendez-vous client (date_rdv) tombe dans le mois affiché, ainsi que les
// rendez-vous libres créés directement depuis l'agenda (voir
// routers/rendezvous_routes.py) — navigation mois précédent/suivant, et
// bouton "Ajouter un rdv" pour créer un rendez-vous sans passer par une
// fiche intervention.
function MonthAgenda() {
  const today = new Date();
  const [viewDate, setViewDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [interventions, setInterventions] = useState([]);
  const [rendezVous, setRendezVous] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addDate, setAddDate] = useState("");
  const [addTitre, setAddTitre] = useState("");
  const [addNotes, setAddNotes] = useState("");
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  // Horloge vive : un rendez-vous passe tout seul en rouge ("en retard") dès
  // que l'heure de fin prévue est dépassée — voir calendarUtils.js > useNow.
  const now = useNow();

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const load = async () => {
    setLoading(true);
    try {
      const monthStart = new Date(year, month, 1);
      const monthEnd = new Date(year, month + 1, 0, 23, 59, 59);
      const [itRes, rdvRes] = await Promise.all([
        api.get("/interventions"),
        api.get("/rendez-vous", { params: { date_from: monthStart.toISOString(), date_to: monthEnd.toISOString() } }),
      ]);
      setInterventions(itRes.data || []);
      setRendezVous(rdvRes.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [year, month]); // eslint-disable-line react-hooks/exhaustive-deps

  const weeks = buildMonthGrid(year, month);

  const eventsForDay = (d) => {
    const dayKey = d.toDateString();
    const fromInterventions = interventions
      .filter((it) => it.date_rdv && new Date(it.date_rdv).toDateString() === dayKey)
      .map((it) => ({ id: `it-${it.id}`, heure: it.date_rdv, label: it.client_nom || it.numero, link: `/interventions/${it.id}`, kind: "intervention" }));
    const fromRdv = rendezVous
      .filter((r) => new Date(r.date_heure).toDateString() === dayKey)
      .map((r) => ({
        id: `rdv-${r.id}`, heure: r.date_heure, label: r.titre, rdvId: r.id, kind: "rdv", notes: r.notes,
        // Conservés pour le calcul du statut visuel (voir rdvVisualStatus) —
        // pas de détection de superposition ici (ce widget agenda ne l'a
        // jamais calculée), seulement "terminé"/"en retard".
        statut: r.statut, duree_minutes: r.duree_minutes, date_heure: r.date_heure,
      }));
    return [...fromInterventions, ...fromRdv].sort((a, b) => new Date(a.heure) - new Date(b.heure));
  };

  const isToday = (d) => d.toDateString() === today.toDateString();
  const isCurrentMonth = (d) => d.getMonth() === month;

  const openAddDialog = (day) => {
    const base = day || new Date();
    const pad = (n) => String(n).padStart(2, "0");
    setAddDate(`${base.getFullYear()}-${pad(base.getMonth() + 1)}-${pad(base.getDate())}T09:00`);
    setAddTitre("");
    setAddNotes("");
    setAddOpen(true);
  };

  const submitAddRdv = async () => {
    if (!addTitre.trim()) { toast.error("Le titre du rendez-vous est obligatoire"); return; }
    if (!addDate) { toast.error("Indiquez une date et une heure"); return; }
    setAddSubmitting(true);
    try {
      await api.post("/rendez-vous", {
        titre: addTitre.trim(),
        date_heure: new Date(addDate).toISOString(),
        notes: addNotes.trim(),
      });
      toast.success("Rendez-vous ajouté");
      setAddOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAddSubmitting(false);
    }
  };

  const deleteRdv = async (rdvId) => {
    try {
      await api.delete(`/rendez-vous/${rdvId}`);
      toast.success("Rendez-vous supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 h-full" data-testid="dashboard-agenda">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewDate(new Date(year, month - 1, 1))}
            className="p-1.5 rounded-lg border border-border hover:bg-accent transition-colors"
            data-testid="agenda-prev-month"
            aria-label="Mois précédent"
          >
            <ChevronLeft size={16} />
          </button>
          <h3 className="font-heading font-semibold flex items-center gap-2 min-w-[170px] justify-center">
            <Calendar size={16} className="text-primary" /> {MONTH_LABELS[month]} {year}
          </h3>
          <button
            onClick={() => setViewDate(new Date(year, month + 1, 1))}
            className="p-1.5 rounded-lg border border-border hover:bg-accent transition-colors"
            data-testid="agenda-next-month"
            aria-label="Mois suivant"
          >
            <ChevronRight size={16} />
          </button>
        </div>
        <div className="flex items-center gap-3">
          <Button size="sm" className="gap-1.5" onClick={() => openAddDialog(null)} data-testid="agenda-add-rdv-button">
            <Plus size={14} />Ajouter un rdv
          </Button>
          <Link to="/interventions" className="text-xs text-primary hover:underline">Voir tout</Link>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Chargement...</p>
      ) : (
        <>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DOW_LABELS.map((d) => (
              <p key={d} className="text-center text-[11px] font-semibold text-muted-foreground">{d}</p>
            ))}
          </div>
          <div className="space-y-1">
            {weeks.map((week, wi) => (
              <div key={wi} className="grid grid-cols-7 gap-1">
                {week.map((d, di) => {
                  const events = eventsForDay(d);
                  return (
                    <button
                      key={di}
                      onClick={() => setSelectedDay(d)}
                      className={`min-h-[64px] text-left rounded-lg border p-1 space-y-0.5 transition-colors ${
                        isToday(d) ? "border-primary/50 bg-primary/5" : "border-border hover:bg-accent/50"
                      } ${!isCurrentMonth(d) ? "opacity-40" : ""}`}
                      data-testid={`agenda-day-${d.toISOString().slice(0, 10)}`}
                    >
                      <p className={`text-[11px] font-medium ${isToday(d) ? "text-primary" : ""}`}>{d.getDate()}</p>
                      {events.slice(0, 2).map((ev) => {
                        const chipClass = ev.kind === "rdv" ? RDV_STATUS_CHIP_CLASSES[rdvVisualStatus(ev, { now })] : "bg-primary/10 text-primary";
                        return (
                          <p key={ev.id} className={`text-[9px] leading-tight truncate rounded px-1 py-0.5 ${chipClass}`}>
                            {new Date(ev.heure).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} {ev.label}
                          </p>
                        );
                      })}
                      {events.length > 2 && (
                        <p className="text-[9px] text-muted-foreground pl-1">+{events.length - 2} autre(s)</p>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </>
      )}

      <Dialog open={!!selectedDay} onOpenChange={(v) => !v && setSelectedDay(null)}>
        <DialogContent data-testid="agenda-day-dialog">
          <DialogHeader>
            <DialogTitle>{selectedDay && `${selectedDay.getDate()} ${MONTH_LABELS[selectedDay.getMonth()]} ${selectedDay.getFullYear()}`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {selectedDay && eventsForDay(selectedDay).length === 0 && (
              <p className="text-sm text-muted-foreground">Aucun rendez-vous ce jour-là.</p>
            )}
            {selectedDay && eventsForDay(selectedDay).map((ev) => {
              const status = ev.kind === "rdv" ? rdvVisualStatus(ev, { now }) : null;
              return (
                <div
                  key={ev.id}
                  className={`flex items-center justify-between gap-2 rounded-lg border p-2 text-sm ${
                    status === "termine" ? "border-emerald-500/60 bg-emerald-500/10"
                      : status === "en_retard" ? "border-red-500/60 bg-red-500/10"
                      : "border-border"
                  }`}
                >
                  <div className="min-w-0">
                    <p className="font-medium truncate">
                      {new Date(ev.heure).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} — {ev.label}
                    </p>
                    {ev.notes && <p className="text-xs text-muted-foreground truncate">{ev.notes}</p>}
                    {status === "termine" && <p className="text-xs text-emerald-600 dark:text-emerald-400">Terminé (vente clôturée)</p>}
                    {status === "en_retard" && <p className="text-xs text-red-600 dark:text-red-400">En retard — vente non clôturée</p>}
                  </div>
                  {ev.kind === "intervention" ? (
                    <Link to={ev.link} className="text-xs text-primary hover:underline shrink-0">Ouvrir</Link>
                  ) : (
                    <button onClick={() => deleteRdv(ev.rdvId)} className="text-destructive shrink-0" title="Supprimer">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => { const d = selectedDay; setSelectedDay(null); openAddDialog(d); }}>
              <Plus size={14} />Ajouter un rdv ce jour-là
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="agenda-add-rdv-dialog">
          <DialogHeader><DialogTitle>Ajouter un rendez-vous</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Titre (ex. Appel client, RDV commercial...)"
              value={addTitre}
              onChange={(e) => setAddTitre(e.target.value)}
              autoFocus
              data-testid="agenda-add-rdv-titre"
            />
            <Input
              type="datetime-local"
              value={addDate}
              onChange={(e) => setAddDate(e.target.value)}
              data-testid="agenda-add-rdv-date"
            />
            <Textarea
              placeholder="Notes (optionnel)"
              value={addNotes}
              onChange={(e) => setAddNotes(e.target.value)}
              rows={2}
              data-testid="agenda-add-rdv-notes"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>Annuler</Button>
            <Button onClick={submitAddRdv} disabled={addSubmitting} data-testid="agenda-add-rdv-submit">
              {addSubmitting ? "Ajout..." : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Activité récente : fusionne, triées par date décroissante, les ventes
// récentes, les tâches récemment mises à jour et les derniers messages —
// un seul flux chronologique plutôt que trois cartes séparées.
// --- Pointage (badgeage arrivée/départ) — voir routers/pointage_routes.py.
// Chaque utilisateur pointe pour lui-même ; la durée écoulée depuis l'arrivée
// se met à jour en direct pendant qu'il est pointé.
function PointageWidget() {
  const [statut, setStatut] = useState(null);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(Date.now());

  const load = () => {
    api.get("/pointage/statut").then((r) => setStatut(r.data)).catch(() => setStatut(null));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(interval);
  }, []);

  const pointer = async (action) => {
    setLoading(true);
    try {
      await api.post(`/pointage/${action}`);
      toast.success(action === "entree" ? "Arrivée enregistrée" : "Départ enregistré");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const duree = statut?.pointe
    ? Math.max(0, Math.round((now - new Date(statut.heure_arrivee).getTime()) / 60000))
    : 0;

  return (
    <div className="rounded-xl border border-border bg-card p-5 h-full flex flex-col justify-between" data-testid="dashboard-pointage">
      <div>
        <p className="font-heading font-semibold flex items-center gap-2 mb-3"><Clock size={16} className="text-primary" />Pointage</p>
        {statut?.pointe ? (
          <div>
            <p className="text-sm text-emerald-600 font-medium mb-1">Pointé depuis {new Date(statut.heure_arrivee).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</p>
            <p className="text-xs text-muted-foreground">{Math.floor(duree / 60)}h{String(duree % 60).padStart(2, "0")} écoulées</p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Non pointé actuellement</p>
        )}
        {statut?.prevu && (
          <p className="text-xs text-muted-foreground mt-1" data-testid="dashboard-pointage-prevu">
            Horaire prévu : {statut.prevu.debut.slice(11, 16)} – {statut.prevu.fin.slice(11, 16)}{statut.prevu.nuit ? " (nuit)" : ""}
          </p>
        )}
        {statut?.oubli && (
          <p className="text-xs text-red-600 mt-2" data-testid="dashboard-pointage-oubli">
            Sortie du {statut.oubli.heure_arrivee.slice(8, 10)}/{statut.oubli.heure_arrivee.slice(5, 7)} non pointée : signalée à votre gestionnaire.
          </p>
        )}
        <Link to="/mon-pointage" className="text-xs text-primary hover:underline mt-2 inline-block" data-testid="dashboard-pointage-link">Voir mon détail</Link>
      </div>
      <Button
        className="mt-4 gap-2"
        variant={statut?.pointe ? "outline" : "default"}
        disabled={loading}
        onClick={() => pointer(statut?.pointe ? "sortie" : "entree")}
        data-testid="dashboard-pointage-button"
      >
        <Clock size={15} />{statut?.pointe ? "Pointer la sortie" : "Pointer l'arrivée"}
      </Button>
    </div>
  );
}

// --- Objectifs de vente personnels — voir routers/objectifs_routes.py.
function MesObjectifsWidget() {
  const [objectifs, setObjectifs] = useState([]);

  useEffect(() => {
    const mois = new Date().toISOString().slice(0, 7);
    api.get("/objectifs/mes-objectifs", { params: { mois } }).then((r) => setObjectifs(r.data || [])).catch(() => setObjectifs([]));
  }, []);

  if (objectifs.length === 0) return null;

  return (
    <div className="rounded-xl border border-border bg-card p-5 h-full" data-testid="dashboard-objectifs">
      <p className="font-heading font-semibold flex items-center gap-2 mb-3"><Euro size={16} className="text-primary" />Objectifs du mois</p>
      <div className="space-y-3">
        {objectifs.map((o) => (
          <div key={o.id} data-testid={`objectif-progress-${o.id}`}>
            <div className="flex justify-between text-xs text-muted-foreground mb-1">
              <span>{o.user_id ? "Objectif personnel" : "Objectif boutique"}</span>
              <span>{o.montant_realise.toFixed(0)} € / {o.montant_cible.toFixed(0)} €</span>
            </div>
            <div className="h-2 rounded-full bg-secondary overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${o.progression_percent >= 100 ? "bg-emerald-500" : "bg-primary"}`}
                style={{ width: `${o.progression_percent}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ActivityFeed({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const calls = [
        hasPerm(user, "caisse", "access") ? api.get("/caisse") : Promise.resolve({ data: [] }),
        hasPerm(user, "taches", "view") ? api.get("/taches") : Promise.resolve({ data: [] }),
        api.get("/communication/messages"),
      ];
      const [ticketsR, tachesR, messagesR] = await Promise.allSettled(calls);

      const val = (r) => (r.status === "fulfilled" ? r.value.data : []);
      const tickets = val(ticketsR).map((t) => ({
        kind: "vente", id: `t-${t.id}`, ts: t.created_at,
        label: `Vente ${t.numero} — ${(t.total_ttc ?? 0).toFixed(2)} €`,
      }));
      const taches = val(tachesR)
        .filter((t) => t.updated_at)
        .map((t) => ({
          kind: "tache", id: `k-${t.id}`, ts: t.updated_at,
          label: `« ${t.nom} » — ${TASK_COLUMNS.find((c) => c.value === t.statut)?.label || t.statut}`,
        }));
      const messages = val(messagesR).map((m) => ({
        kind: "message", id: `m-${m.id}`, ts: m.created_at,
        label: `${m.from_user_id === user.id ? "Vous" : m.from_user_nom} : ${m.content ? m.content : m.attachment_name ? `a envoyé ${m.attachment_name}` : "message"}`,
      }));

      const merged = [...tickets, ...taches, ...messages]
        .filter((i) => i.ts)
        .sort((a, b) => new Date(b.ts) - new Date(a.ts))
        .slice(0, 8);
      setItems(merged);
      setLoading(false);
    })();
  }, [user]);

  const ICONS = { vente: Receipt, tache: ListChecks, message: MessageSquare };

  return (
    <div className="rounded-xl border border-border bg-card p-5 h-full" data-testid="dashboard-activity-feed">
      <h3 className="font-heading font-semibold flex items-center gap-2 mb-3"><ClipboardList size={16} className="text-primary" /> Activité récente</h3>
      {loading ? (
        <p className="text-sm text-muted-foreground">Chargement...</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Aucune activité récente.</p>
      ) : (
        <div className="space-y-2">
          {items.map((i) => {
            const Icon = ICONS[i.kind];
            return (
              <div key={i.id} className="flex items-start gap-2.5 text-sm py-1.5 border-b border-border last:border-0">
                <span className="shrink-0 mt-0.5 rounded-full bg-primary/10 text-primary p-1"><Icon size={12} /></span>
                <p className="flex-1 min-w-0 truncate">{i.label}</p>
                <span className="text-[10px] text-muted-foreground shrink-0 whitespace-nowrap mt-0.5">{timeAgo(i.ts)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatutBadge({ statut }) {
  const label = TASK_COLUMNS.find((c) => c.value === statut)?.label || statut;
  return (
    <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border shrink-0 ${STATUT_BADGE[statut] || STATUT_BADGE.non_assigne}`}>
      {label}
    </span>
  );
}

function TaskRow({ t, onOpen }) {
  const duree = dureeInfo(t);
  return (
    <button
      onClick={() => onOpen(t)}
      className="w-full flex items-center gap-3 text-left rounded-lg border border-border bg-background px-3 py-2.5 hover:border-primary/50 transition-colors"
      data-testid={`task-row-${t.id}`}
    >
      <span className={`shrink-0 w-2 h-2 rounded-full ${t.assigned_to ? "bg-primary" : "bg-muted-foreground/40"}`} title={t.assigned_to ? "Assigné" : "Non assigné"} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{t.nom}</p>
        <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground mt-0.5">
          <span className="flex items-center gap-1"><User size={11} />{t.assigned_nom || "Non assigné"}</span>
          {t.temps_estime && <span className="flex items-center gap-1"><Clock size={11} />{t.temps_estime}</span>}
          {t.attachments?.length > 0 && <span className="flex items-center gap-1"><Paperclip size={11} />{t.attachments.length}</span>}
          {t.linked_document && <span className="flex items-center gap-1"><LinkIcon size={11} />{t.linked_document.label}</span>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {duree && (
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${duree.className}`} data-testid={`task-duree-${t.id}`}>
            {duree.label}
          </span>
        )}
        <StatutBadge statut={t.statut} />
      </div>
    </button>
  );
}

const SORT_OPTIONS = [
  { value: "statut", label: "Statut" },
  { value: "non_assigne", label: "Non assigné d'abord" },
  { value: "utilisateur", label: "Utilisateur assigné" },
  { value: "duree", label: "Durée (urgence)" },
];
const STATUT_ORDER = { non_assigne: 0, en_attente: 1, en_cours: 2, termine: 3 };

function sortTaches(list, sortBy) {
  const arr = [...list];
  switch (sortBy) {
    case "non_assigne":
      return arr.sort((a, b) => (a.assigned_to ? 1 : 0) - (b.assigned_to ? 1 : 0));
    case "utilisateur":
      return arr.sort((a, b) => (a.assigned_nom || "\uffff").localeCompare(b.assigned_nom || "\uffff"));
    case "duree": {
      const pct = (t) => dureeInfo(t)?.pct ?? -1;
      return arr.sort((a, b) => pct(b) - pct(a));
    }
    case "statut":
    default:
      return arr.sort((a, b) => STATUT_ORDER[a.statut] - STATUT_ORDER[b.statut]);
  }
}

function TaskDetailDialog({ open, onOpenChange, tache, users, onSaved, onDeleted, canAssign, canEdit, canAvancement, canAttachments, canDelete }) {
  const isNew = !tache;
  const [form, setForm] = useState({ nom: "", description: "", temps_estime: "", statut: "en_attente", assigned_to: "" });
  const [linkType, setLinkType] = useState("devis");
  const [linkQuery, setLinkQuery] = useState("");
  const [linkResults, setLinkResults] = useState([]);
  const [linking, setLinking] = useState(false);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (tache) {
      setForm({
        nom: tache.nom,
        description: tache.description || "",
        temps_estime: tache.temps_estime || "",
        statut: tache.statut === "non_assigne" ? "en_attente" : tache.statut,
        assigned_to: tache.assigned_to || "",
      });
    } else {
      setForm({ nom: "", description: "", temps_estime: "", statut: "en_attente", assigned_to: "" });
    }
    setLinking(false);
    setLinkQuery("");
    setLinkResults([]);
  }, [tache, open]);

  const searchLinks = async (q) => {
    setLinkQuery(q);
    if (!q.trim()) { setLinkResults([]); return; }
    try {
      const { data } = await api.get("/taches/documents/search", { params: { type: linkType, q } });
      setLinkResults(data);
    } catch (e) { /* silencieux */ }
  };

  const save = async () => {
    if (!form.nom.trim()) { toast.error("Le nom de la tâche est obligatoire"); return; }
    try {
      let saved;
      if (tache) {
        // Sur une tâche existante on n'envoie que les champs que l'utilisateur a
        // le droit de modifier, pour ne jamais déclencher une vérification de
        // permission sur un champ resté inchangé (ex : un utilisateur n'ayant
        // que le droit "avancement" ne doit pas se voir refuser l'enregistrement
        // à cause du champ "nom", même envoyé avec sa valeur d'origine).
        const payload = {};
        if (canEdit) {
          payload.nom = form.nom;
          payload.description = form.description;
          payload.temps_estime = form.temps_estime;
        }
        if (canAssign) payload.assigned_to = form.assigned_to || null;
        if (canEdit || canAvancement) payload.statut = form.statut;
        const { data } = await api.patch(`/taches/${tache.id}`, payload);
        saved = data;
      } else {
        const payload = { nom: form.nom, description: form.description, temps_estime: form.temps_estime, statut: form.statut, assigned_to: form.assigned_to || null };
        const { data } = await api.post("/taches", payload);
        saved = data;
      }
      toast.success("Tâche enregistrée");
      onSaved(saved);
      if (isNew) onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const attachLink = async (doc) => {
    try {
      const { data } = await api.patch(`/taches/${tache.id}`, { linked_document: { type: linkType, id: doc.id } });
      toast.success("Document lié");
      onSaved(data);
      setLinking(false);
      setLinkQuery("");
      setLinkResults([]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const removeLink = async () => {
    try {
      const { data } = await api.patch(`/taches/${tache.id}`, { clear_linked_document: true });
      toast.success("Document dissocié");
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !tache) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/taches/${tache.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Fichier joint");
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeFile = async (attId) => {
    try {
      const { data } = await api.delete(`/taches/${tache.id}/attachments/${attId}`);
      toast.success("Pièce jointe supprimée");
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async () => {
    if (!tache || !window.confirm("Supprimer cette tâche ?")) return;
    try {
      await api.delete(`/taches/${tache.id}`);
      toast.success("Tâche supprimée");
      onDeleted(tache.id);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const documentLinkUrl = (link) => {
    if (!link) return null;
    if (link.type === "devis") return `/devis/${link.id}`;
    if (link.type === "reprise") return `/reprises/${link.id}`;
    if (link.type === "intervention") return `/interventions/${link.id}`;
    return null;
  };

  // Sur une tâche existante, les champs (hors assignation) ne sont modifiables
  // qu'avec la permission "taches.edit" ; à la création, ils sont toujours
  // actifs (le bouton "Nouvelle tâche" est lui-même masqué sans "taches.create").
  const fieldsLocked = !isNew && !canEdit;
  // L'avancement (statut) peut être modifié avec "taches.edit" OU la
  // permission dédiée "taches.avancement", indépendamment des autres champs.
  const statutLocked = !isNew && !canEdit && !canAvancement;
  // Les pièces jointes peuvent être ajoutées/retirées avec "taches.edit" OU la
  // permission dédiée "taches.attachments".
  const canManageAttachments = canEdit || canAttachments;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="task-detail-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isNew ? "Nouvelle tâche" : "Détail de la tâche"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Nom</label>
            <Input data-testid="task-form-nom" placeholder="Nom de la tâche" value={form.nom} disabled={fieldsLocked} onChange={(e) => setForm({ ...form, nom: e.target.value })} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Description</label>
            <Textarea data-testid="task-form-description" placeholder="Ce qu'il y a à faire (optionnel)" value={form.description} disabled={fieldsLocked} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Temps pour la faire</label>
              <Input data-testid="task-form-temps" placeholder="ex : 30 min, 2h, 1j" value={form.temps_estime} disabled={fieldsLocked} onChange={(e) => setForm({ ...form, temps_estime: e.target.value })} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Assigner à</label>
              {canAssign ? (
                <Select value={form.assigned_to || "none"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="task-form-assigned"><SelectValue placeholder="Non assigné" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Non assigné</SelectItem>
                    {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.prenom} {u.nom}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm px-3 py-2 rounded-md border border-border bg-secondary/40 text-muted-foreground" data-testid="task-form-assigned-readonly">
                  {tache?.assigned_nom || "Non assigné"}
                </p>
              )}
            </div>
          </div>

          {form.assigned_to ? (
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Avancement</label>
              <Select value={MANUAL_STATUTS.some((c) => c.value === form.statut) ? form.statut : "en_attente"} disabled={statutLocked} onValueChange={(v) => setForm({ ...form, statut: v })}>
                <SelectTrigger data-testid="task-form-statut"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MANUAL_STATUTS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">
              {isNew
                ? "Sans assignation, la tâche reste \"Non assigné\". Dès qu'un utilisateur est assigné, elle passe automatiquement en \"En attente\"."
                : "Cette tâche n'est pas assignée."}
            </p>
          )}

          {!isNew && (
            <>
              <div className="space-y-1.5 pt-1 border-t border-border">
                <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5 pt-2"><LinkIcon size={12} /> Document lié</label>
                {tache.linked_document ? (
                  <div className="flex items-center justify-between text-sm rounded-md border border-border px-2.5 py-1.5">
                    <a href={documentLinkUrl(tache.linked_document)} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-primary hover:underline truncate" data-testid="task-linked-doc">
                      {tache.linked_document.label} <ExternalLink size={11} />
                    </a>
                    {canEdit && <button onClick={removeLink} className="text-muted-foreground hover:text-destructive shrink-0" data-testid="task-unlink-doc"><X size={13} /></button>}
                  </div>
                ) : linking ? (
                  <div className="space-y-1.5">
                    <div className="flex gap-1.5">
                      <Select value={linkType} onValueChange={(v) => { setLinkType(v); setLinkQuery(""); setLinkResults([]); }}>
                        <SelectTrigger className="w-32 shrink-0" data-testid="task-link-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {DOCUMENT_TYPES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div className="relative flex-1">
                        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input className="pl-8" placeholder="N° ou nom du client..." value={linkQuery} onChange={(e) => searchLinks(e.target.value)} data-testid="task-link-search" />
                      </div>
                    </div>
                    {linkResults.length > 0 && (
                      <div className="border border-border rounded-md divide-y divide-border max-h-32 overflow-y-auto">
                        {linkResults.map((d) => (
                          <button key={d.id} onClick={() => attachLink(d)} className="w-full text-left text-sm px-2.5 py-1.5 hover:bg-secondary" data-testid={`task-link-result-${d.id}`}>
                            {d.numero} {d.client_nom && `— ${d.client_nom}`}
                          </button>
                        ))}
                      </div>
                    )}
                    <button onClick={() => setLinking(false)} className="text-xs text-muted-foreground hover:underline">Annuler</button>
                  </div>
                ) : canEdit ? (
                  <Button size="sm" variant="outline" onClick={() => setLinking(true)} className="gap-1.5" data-testid="task-link-open">
                    <LinkIcon size={13} /> Lier un devis, une reprise ou une intervention
                  </Button>
                ) : (
                  <p className="text-xs text-muted-foreground italic">Aucun document lié.</p>
                )}
              </div>

              <div className="space-y-1.5 pt-1 border-t border-border">
                <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5 pt-2"><Paperclip size={12} /> Fichiers joints</label>
                <div className="space-y-1">
                  {(tache.attachments || []).map((a) => (
                    <div key={a.id} className="flex items-center justify-between text-sm rounded-md border border-border px-2.5 py-1.5" data-testid={`task-attachment-${a.id}`}>
                      <a href={`${UPLOADS_BASE}${a.url}`} target="_blank" rel="noreferrer" className="truncate text-primary hover:underline">{a.name}</a>
                      {canManageAttachments && <button onClick={() => removeFile(a.id)} className="text-muted-foreground hover:text-destructive shrink-0" data-testid={`task-attachment-remove-${a.id}`}><X size={13} /></button>}
                    </div>
                  ))}
                  {(tache.attachments || []).length === 0 && <p className="text-xs text-muted-foreground italic">Aucun fichier joint.</p>}
                </div>
                {canManageAttachments && (
                  <>
                    <input ref={fileRef} type="file" className="hidden" onChange={uploadFile} data-testid="task-attachment-input" />
                    <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-1.5">
                      <Upload size={13} /> {uploading ? "Envoi..." : "Joindre un fichier (image, PDF...)"}
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
        <DialogFooter className="flex items-center justify-between sm:justify-between">
          {!isNew && canDelete ? (
            <Button variant="ghost" className="text-destructive hover:text-destructive gap-1.5" onClick={remove} data-testid="task-detail-delete">
              <Trash2 size={14} /> Supprimer
            </Button>
          ) : <span />}
          {(isNew || canEdit || canAssign || canAvancement) && <Button data-testid="task-form-save" onClick={save}>Enregistrer</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TaskBoard() {
  const { user } = useAuth();
  const canCreate = hasPerm(user, "taches", "create");
  const canAssign = hasPerm(user, "taches", "assign");
  const canEdit = hasPerm(user, "taches", "edit");
  const canAvancement = hasPerm(user, "taches", "avancement");
  const canAttachments = hasPerm(user, "taches", "attachments");
  const canDelete = hasPerm(user, "taches", "delete");
  const [taches, setTaches] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [statutFilter, setStatutFilter] = useState("tous");
  const [sortBy, setSortBy] = useState("statut");
  const [view, setView] = useState("columns");
  const [, setTick] = useState(0);

  const load = async () => {
    const { data } = await api.get("/taches");
    setTaches(data);
  };
  useEffect(() => {
    load();
    api.get("/users").then((r) => setUsers(r.data));
    // Rafraîchit l'affichage chaque minute pour que le code couleur de durée
    // (bleu -> orange -> rouge "en retard") évolue en direct sans recharger la page.
    const interval = setInterval(() => setTick((n) => n + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  const openCreate = () => { setSelected(null); setOpen(true); };
  const openTask = (t) => { setSelected(t); setOpen(true); };

  const handleSaved = (saved) => {
    setSelected(saved);
    setTaches((list) => {
      const exists = list.some((t) => t.id === saved.id);
      return exists ? list.map((t) => (t.id === saved.id ? saved : t)) : [saved, ...list];
    });
  };
  const handleDeleted = (id) => setTaches((list) => list.filter((t) => t.id !== id));

  const filtered = sortTaches(
    taches.filter((t) => statutFilter === "tous" || t.statut === statutFilter),
    sortBy
  );

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="dashboard-tasks">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-heading font-semibold flex items-center gap-2"><ListChecks size={16} className="text-primary" /> Tâches</h3>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-lg border border-border p-0.5">
            <button
              onClick={() => setView("columns")}
              data-testid="task-view-columns"
              title="Vue colonnes"
              className={`p-1.5 rounded-md ${view === "columns" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              <LayoutGrid size={14} />
            </button>
            <button
              onClick={() => setView("list")}
              data-testid="task-view-list"
              title="Vue liste"
              className={`p-1.5 rounded-md ${view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              <List size={14} />
            </button>
          </div>
          {canCreate && <Button size="sm" onClick={openCreate} className="gap-1.5" data-testid="task-add-button"><Plus size={14} /> Nouvelle tâche</Button>}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {view === "list" && (
          <div className="flex items-center gap-1.5">
            <Filter size={13} className="text-muted-foreground" />
            <Select value={statutFilter} onValueChange={setStatutFilter}>
              <SelectTrigger className="h-8 w-40 text-xs" data-testid="task-filter-statut"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tous">Tous les statuts</SelectItem>
                {TASK_COLUMNS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <ArrowUpDown size={13} className="text-muted-foreground" />
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-8 w-48 text-xs" data-testid="task-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {view === "columns" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="task-columns">
          {TASK_COLUMNS.map((col) => {
            const colTasks = sortTaches(taches.filter((t) => t.statut === col.value), sortBy);
            return (
              <div key={col.value} className="rounded-lg bg-background border border-border p-2.5" data-testid={`task-column-${col.value}`}>
                <div className="flex items-center justify-between mb-2 px-0.5">
                  <p className="text-xs font-semibold text-muted-foreground">{col.label}</p>
                  <span className="text-[10px] text-muted-foreground bg-secondary rounded-full px-1.5 py-0.5">{colTasks.length}</span>
                </div>
                <div className="space-y-1.5 min-h-[40px]">
                  {colTasks.length === 0 && <p className="text-[11px] text-muted-foreground/60 italic px-1">Aucune tâche.</p>}
                  {colTasks.map((t) => <TaskRow key={t.id} t={t} onOpen={openTask} />)}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-2" data-testid="task-list">
          {filtered.length === 0 && <p className="text-sm text-muted-foreground/70 italic">Aucune tâche.</p>}
          {filtered.map((t) => <TaskRow key={t.id} t={t} onOpen={openTask} />)}
        </div>
      )}

      <TaskDetailDialog
        open={open}
        onOpenChange={setOpen}
        tache={selected}
        users={users}
        onSaved={handleSaved}
        onDeleted={handleDeleted}
        canAssign={canAssign}
        canEdit={canEdit}
        canAvancement={canAvancement}
        canAttachments={canAttachments}
        canDelete={canDelete}
      />
    </div>
  );
}

export default function Dashboard() {
  const { user, modules } = useAuth();
  const [stats, setStats] = useState({ articles: 0, tickets: 0, interventions: 0, devis: 0, reprises: 0, depot: 0 });
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/dashboard/overview").then((r) => setOverview(r.data)).catch(() => setOverview(null));
  }, [user]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const calls = [
        api.get("/stock"),
        api.get("/caisse"),
        api.get("/interventions"),
        api.get("/devis"),
        api.get("/reprises"),
        hasPerm(user, "depot") ? api.get("/depot/orders") : Promise.resolve({ data: [] }),
      ];
      const results = await Promise.allSettled(calls);
      const val = (r) => (r.status === "fulfilled" ? r.value.data.length : 0);
      setStats({
        articles: val(results[0]),
        tickets: val(results[1]),
        interventions: val(results[2]),
        devis: val(results[3]),
        reprises: val(results[4]),
        depot: val(results[5]),
      });
    })();
  }, [user]);

  return (
    <Layout title={`Bonjour, ${user?.prenom || ""}`}>
      {overview && (overview.ca_jour || overview.devis_en_attente !== undefined || overview.commandes_a_receptionner !== undefined || overview.stock_bas !== undefined) && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="dashboard-overview">
          {overview.ca_jour && (
            <OverviewCard
              icon={Euro}
              label={`CA du jour · ${overview.ca_jour.nb_ventes} vente(s)`}
              value={`${overview.ca_jour.ca_ttc.toFixed(2)} €`}
              to="/statistiques"
              testId="overview-ca-jour"
            />
          )}
          {overview.devis_en_attente !== undefined && (
            <OverviewCard
              icon={ClipboardList}
              label="Devis en attente"
              value={overview.devis_en_attente}
              tone={overview.devis_en_attente > 0 ? "warning" : "default"}
              to="/devis"
              testId="overview-devis"
            />
          )}
          {overview.commandes_a_receptionner !== undefined && (
            <OverviewCard
              icon={Warehouse}
              label="Commandes à réceptionner"
              value={overview.commandes_a_receptionner}
              tone={overview.commandes_a_receptionner > 0 ? "warning" : "default"}
              to="/commandes"
              testId="overview-commandes"
            />
          )}
          {overview.stock_bas !== undefined && (
            <OverviewCard
              icon={BellRing}
              label="Articles à réapprovisionner"
              value={overview.stock_bas}
              tone={overview.stock_bas > 0 ? "warning" : "default"}
              to="/stock"
              testId="overview-stock-bas"
            />
          )}
        </div>
      )}

      <QuickActions user={user} modules={modules} />

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-6">
        <StatCard icon={Package} label="Articles en stock" value={stats.articles} testId="stat-articles" />
        <StatCard icon={ShoppingCart} label="Tickets / Factures" value={stats.tickets} testId="stat-tickets" />
        <StatCard icon={Wrench} label="Interventions" value={stats.interventions} testId="stat-interventions" />
        <StatCard icon={FileText} label="Devis" value={stats.devis} testId="stat-devis" />
        <StatCard icon={RefreshCcw} label="Reprises" value={stats.reprises} testId="stat-reprises" />
        <StatCard icon={Warehouse} label="Commandes dépôt" value={stats.depot} testId="stat-depot" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
        <PointageWidget />
        <MesObjectifsWidget />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2">
          {hasPerm(user, "intervention", "view") && <MonthAgenda />}
        </div>
        <div className="lg:col-span-1">
          {user && <ActivityFeed user={user} />}
        </div>
      </div>

      <div className="mb-6">
        {hasPerm(user, "taches", "view") && <TaskBoard />}
      </div>
    </Layout>
  );
}
